import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';

// Define types
interface User {
  id: number;
  username: string;
  role: string;
  email?: string;
  name?: string;
}

// Define type for permissions object
interface Permissions {
  [key: string]: boolean;
}

// Define type for the structure coming from rolePermissions.json
interface RolePermission {
  role: string;
  permissions: Permissions;
}

// Define type for Company Settings
interface CompanySettings {
  name?: string;
  logo?: string;
  // ... other settings if needed
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  settingsLoading: boolean;
  error: string | null;
  permissions: Permissions | null;
  companySettings: CompanySettings | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  can: (permissionKey: string) => boolean; // Renamed from isAllowed and changed signature
  hasPermission: (permissionKey: string) => boolean; // Kept for direct use if needed
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  settingsLoading: true,
  error: null,
  permissions: null,
  companySettings: null,
  login: async () => {},
  logout: () => {},
  checkAuth: async () => false,
  can: () => false,
  hasPermission: () => false,
});

// Create provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions(null);
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      logout();
      return false;
    }
  }, [token, logout]);

  // Fetch permissions when user data is available or changes
  useEffect(() => {
    const fetchPermissions = async () => {
      if (user?.role) {
        try {
          // Assuming an endpoint exists to get permissions for the current user/role
          // This might need adjustment based on how permissions are exposed by the backend
          // Option 1: Fetch all permissions and filter by role client-side (if permissions file is small)
          const response = await axios.get('/api/settings/permissions'); // Fetch all role permissions
          const allRolePerms: RolePermission[] = response.data;
          const userRolePerms = allRolePerms.find(rp => rp.role === user.role);
          setPermissions(userRolePerms ? userRolePerms.permissions : null);

          // Option 2: Backend endpoint returns permissions for the logged-in user's role directly
          // const response = await axios.get('/api/auth/my-permissions');
          // setPermissions(response.data);

        } catch (error) {
          console.error("Error fetching user permissions:", error);
          setPermissions(null); // Clear permissions on error
        }
      } else {
        setPermissions(null); // Clear permissions if no user/role
      }
    };

    fetchPermissions();
  }, [user]); // Re-fetch permissions if user object changes

  // Check if user is authenticated on initial load or when token/checkAuth changes
  useEffect(() => {
    setLoading(true); // Set loading true when checking auth
    if (token) {
      checkAuth().finally(() => setLoading(false)); // Ensure loading is false after checkAuth completes
    } else {
      setLoading(false); // No token, not loading
    }
  }, [token, checkAuth]); // Dependencies remain the same

  // Fetch company settings on initial load (only needs to run once)
  useEffect(() => {
    const fetchCompanySettings = async () => {
      setSettingsLoading(true);
      try {
        const response = await axios.get('/api/settings/company'); // Use the correct API endpoint
        setCompanySettings(response.data);
      } catch (error) {
        console.error("Error fetching company settings:", error);
        setCompanySettings(null); // Set to null or default on error
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchCompanySettings();
  }, []); // Empty dependency array ensures this runs only once on mount

  const login = async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      // Permissions will be fetched by the useEffect watching 'user'
    } catch (error: any) {
      setError(error.response?.data?.message || 'Ошибка при входе в систему');
      setPermissions(null); // Clear permissions on login error
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Renamed from isAllowed and updated to check a single permission key
  const can = (permissionKey: string): boolean => {
    if (user?.role === 'Администратор') return true; // Admins have all permissions
    if (!permissions) return false; // No permissions loaded
    // Check if the key exists and is explicitly true
    return permissions[permissionKey] === true;
  };

  // Function to check for a specific permission key (can be used directly or by 'can')
  const hasPermission = (permissionKey: string): boolean => {
    if (user?.role === 'Администратор') return true; // Admins have all permissions
    if (!permissions) return false; // No permissions loaded
    // Check if the key exists and is explicitly true
    return permissions[permissionKey] === true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      settingsLoading,
      error, 
      permissions, 
      companySettings,
      login, 
      logout, 
      checkAuth,
      can, // Updated from isAllowed
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
